import axios, { AxiosInstance } from "axios";
import { loginUrlForCurrentLocation } from "../utils/returnUrl";
import { currentPortalHost } from "./portalHost";

/**
 * Tells the backend which brand portal the browser is on. The sign-in
 * endpoints use it to keep portals isolated: a.reveshare.com must not accept
 * b.reveshare.com's credentials. Read per request, so the local dev override
 * (see lib/portalHost) takes effect without a reload of this module.
 */
function withPortalHost(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use((config) => {
    config.headers["X-Portal-Host"] = currentPortalHost();
    return config;
  });
  return instance;
}

// Base API instance
export const api = withPortalHost(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
  })
);

// Affiliate API instance
export const affiliateApi = withPortalHost(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
  })
);

// Add request interceptor for affiliate requests
affiliateApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("token"); // Make sure this matches your token storage key
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Brand API instance (if you need it)
export const brandApi = withPortalHost(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
  })
);

brandApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("brand_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for brand API - handle 401 (unauthorized) and 403 (forbidden/upgrade required)
brandApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 - clear tokens and redirect to login (only on pricing page)
    if (error.response?.status === 401 && window.location.pathname.includes('/brand/pricing')) {
      // Clear all brand-related tokens
      localStorage.removeItem("brand_user");
      localStorage.removeItem("brand_token");
      localStorage.removeItem("brand_temp_token");
      localStorage.removeItem("brand_refresh_token");
      sessionStorage.removeItem("brand_user");
      sessionStorage.removeItem("brand_token");
      sessionStorage.removeItem("brand_temp_token");
      // Redirect to login page
      window.location.href = "/auth/login";
      return Promise.reject(error);
    }
    
    // Handle 403 - redirect to pricing page when feature is not available on current plan
    if (error.response?.status === 403 && error.response?.data?.upgradeRequired) {
      // Redirect to brand pricing page when feature is not available on current plan
      // Pass query parameter to display upgrade message
      const feature = error.response?.data?.feature || '';
      const currentPlan = error.response?.data?.currentPlan || '';
      const limit = error.response?.data?.limit;
      const currentCount = error.response?.data?.currentCount;
      const params = new URLSearchParams({
        upgradeRequired: 'true',
        ...(feature && { feature }),
        ...(currentPlan && { currentPlan }),
        ...(limit !== undefined && { limit: limit.toString() }),
        ...(currentCount !== undefined && { currentCount: currentCount.toString() }),
      });
      window.location.href = `/brand/pricing?${params.toString()}`;
    }
    return Promise.reject(error);
  }
);

// Agency API instance
export const agencyApi = withPortalHost(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
  })
);

agencyApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("agency_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Admin API instance
export const adminApi = withPortalHost(
  axios.create({
    baseURL: import.meta.env.VITE_API_URL,
  })
);

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add a response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access. Keep the page they were on as returnUrl so
      // an expired session doesn't dump them on the dashboard — a shared join
      // link carries its program in the query string and can't be re-derived.
      localStorage.removeItem("token");
      window.location.href = loginUrlForCurrentLocation();
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for agency API
agencyApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access for agencies
      localStorage.removeItem("agency_token");
      localStorage.removeItem("agency_user");
      window.location.href = "/auth/login";
    }
    return Promise.reject(error);
  }
);